import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';

import spravatoLogo from '../assets/esketamine/spravato_logo.png';
import treatmentRoom from '../assets/esketamine/spravato-treatment.jpg';
import spravatoBackground from '../assets/esketamine/spravato-background.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const highlights = [
  'FDA-approved treatment option',
  'For adults with treatment-resistant depression',
  'Administered in a supervised medical setting',
  'Available only through REMS-certified treatment centers',
  'Monitored before, during, and after each treatment',
  'Care coordinated with your treatment team',
];

const pathway = [
  {
    title: 'Evaluation & Intake',
    description:
      'Our team completes an initial consultation to determine whether SPRAVATO® may be an appropriate option and reviews intake and insurance information.',
  },
  {
    title: 'Treatment Sessions',
    description:
      'SPRAVATO® is administered in a calm, supportive setting while trained medical staff monitor your comfort, safety, and response.',
  },
  {
    title: 'Ongoing Support',
    description:
      'We continue monitoring progress and coordinate care with referring providers when appropriate to support your treatment journey.',
  },
];

const infoCards = [
  {
    title: 'What is SPRAVATO®?',
    description:
      'SPRAVATO® (esketamine) CIII nasal spray is an FDA-approved treatment option for adults with treatment-resistant depression. It may also be used for depressive symptoms in adults with major depressive disorder with acute suicidal thoughts or behavior, depending on clinical appropriateness and treatment planning.',
    image: spravatoLogo,
  },
  {
    title: 'How is Treatment Administered?',
    description:
      'SPRAVATO® is self-administered under the supervision of a healthcare provider. Patients remain in a comfortable treatment area and are monitored for at least two hours after each session. A caregiver, family member, or trusted driver is required to take the patient home after treatment.',
    image: treatmentRoom,
  },
];

const safetyInfo = [
  'SPRAVATO® may cause sleepiness, dizziness, dissociation, nausea, or increased blood pressure.',
  'Because of these risks, SPRAVATO® is only available through the SPRAVATO® REMS program.',
  'Patients are monitored after treatment and should not drive or operate machinery until the next day after a restful sleep.',
  'SPRAVATO® is not approved for use as an anesthetic agent or for use in children.',
  'The effectiveness of SPRAVATO® in preventing suicide or reducing suicidal thoughts has not been demonstrated.',
];

export default function Esketamine() {
  return (
    <>
      <Helmet>
        <title>SPRAVATO® Treatment | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Aspire Behavioral Health offers REMS-certified SPRAVATO® treatment for adults with treatment-resistant depression in a safe, supervised clinical setting."
        />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',

          minHeight: {
            xs: 520,
            sm: 580,
            md: 650,
            lg: 620,
          },

          py: {
            xs: 8,
            sm: 10,
            md: 12,
            lg: 10,
          },

          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Box
          component="img"
          src={spravatoBackground}
          alt="SPRAVATO treatment at Aspire Behavioral Health"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -2,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: -1,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3.5rem' },
              lineHeight: 1.2,
            }}
          >
            SPRAVATO® Treatment
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            A supervised treatment option for adults living with
            treatment-resistant depression.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: 'white',
                            },
                            '&:hover': {
                              borderColor: 'white',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
        {/* Intro */}
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Our SPRAVATO® Program
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            At Aspire Behavioral Health, we are proud to be a REMS-certified
            treatment center offering SPRAVATO® (esketamine) treatment in a
            safe, supportive, and carefully monitored setting.
            <br />
            <br />
            For adults living with treatment-resistant depression, SPRAVATO® may
            offer a different treatment approach when other options have not
            provided enough relief. Each session is supervised by trained medical
            staff, with monitoring before, during, and after treatment.
            <br />
            <br />
            Our care team works with patients, families, and referring providers
            when appropriate to support safety, comfort, and progress throughout
            the treatment journey.
            <br />
            <br />
            To learn more or schedule an evaluation, call{' '}
            <a href="tel:2108669850">210-866-9850</a>.
          </Typography>
        </Box>

        <Divider
          sx={{
            width: { xs: '80%', sm: '60%', md: '40%' },
            mx: 'auto',
            mb: 7,
            borderColor: '#d9d9d9',
          }}
        />

        {/* Highlights */}
        <Box
          sx={{
            backgroundColor: '#e3f3c5',
            color: 'black',
            maxWidth: '1200px',
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            mb: 8,
          }}
        >
          <Box
            component="img"
            src={spravatoLogo}
            alt="SPRAVATO Logo"
            sx={{
              display: 'block',
              mx: 'auto',
              width: '100%',
              maxWidth: 230,
              mb: 3,
            }}
          />

          <Typography
            variant="h4"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
          >
            SPRAVATO® at a Glance
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            SPRAVATO® is provided only in certified treatment settings with
            supervision and monitoring from trained healthcare professionals.
          </Typography>

          <Grid container spacing={2} justifyContent="center" alignItems="stretch">
            {highlights.map((item) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={item}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    maxWidth: 340,
                    borderRadius: 3,
                    boxShadow: 'none',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Info Cards */}
        <Box sx={{ maxWidth: '1000px', mx: 'auto', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            About Treatment
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            Our team will help determine whether SPRAVATO® may be an appropriate
            option based on your symptoms, history, and treatment needs.
          </Typography>

          {infoCards.map((item) => (
            <Card
              key={item.title}
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                mb: 4,
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, sm: 5 },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {item.image && (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      maxWidth: 260,
                      height: 'auto',
                      borderRadius: 3,
                      objectFit: 'contain',
                    }}
                  />
                )}

                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>

                  <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
                    {item.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Care Pathway */}
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            The Care Pathway
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            From evaluation through ongoing treatment, our team supports you
            through each step.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {pathway.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: '#3a4c17',
                      }}
                    >
                      Step {index + 1}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Safety Information */}
        <Box
          sx={{
            maxWidth: '1000px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
              backgroundColor: '#f7f7f7',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <Typography
                variant="h4"
                sx={{
                  textAlign: 'center',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Important Safety Information
              </Typography>

              <Grid container spacing={2}>
                {safetyInfo.map((item) => (
                  <Grid item xs={12} key={item}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: 'none',
                        backgroundColor: 'white',
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.7,
                            color: 'text.secondary',
                          }}
                        >
                          {item}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to learn more about SPRAVATO®?
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}